import React, { useState } from 'react';
import { InstagramIcon, TikTokIcon, YouTubeIcon, LinkIcon } from '../icons';
import SpecActionSheet from '../SpecActionSheet/SpecActionSheet';
import type { ActionSheetItem } from '../SpecActionSheet/SpecActionSheet';
import './SocialLinksRow.css';

export type SocialPlatform = 'instagram' | 'tiktok' | 'youtube' | 'website';

interface SocialLinksRowProps {
  instagramUrl?: string;
  tiktokUrl?: string;
  youtubeUrl?: string;
  websiteUrl?: string;
  isOwner?: boolean;
  onEdit?: (platform: SocialPlatform, currentUrl: string) => void;
  onDelete?: (platform: SocialPlatform) => void;
  className?: string;
}

const platformLabels: Record<SocialPlatform, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  website: 'Website',
};

export const SocialLinksRow: React.FC<SocialLinksRowProps> = ({
  instagramUrl,
  tiktokUrl,
  youtubeUrl,
  websiteUrl,
  isOwner = false,
  onEdit,
  onDelete,
  className,
}) => {
  const [actionSheetOpen, setActionSheetOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<{ platform: SocialPlatform; url: string } | null>(null);

  const hasAnyLink = instagramUrl || tiktokUrl || youtubeUrl || websiteUrl;

  if (!hasAnyLink) {
    return null;
  }

  const handleClick = (platform: SocialPlatform, url: string) => {
    if (isOwner) {
      setSelectedPlatform({ platform, url });
      setActionSheetOpen(true);
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const actionItems: ActionSheetItem[] = selectedPlatform ? [
    {
      key: 'open',
      label: `Open ${platformLabels[selectedPlatform.platform]}`,
      onClick: () => window.open(selectedPlatform.url, '_blank', 'noopener,noreferrer'),
    },
    {
      key: 'edit',
      label: 'Edit',
      onClick: () => onEdit?.(selectedPlatform.platform, selectedPlatform.url),
    },
    {
      key: 'delete',
      label: 'Remove',
      danger: true,
      onClick: () => onDelete?.(selectedPlatform.platform),
    },
  ] : [];

  return (
    <>
      <div className={`social-links-row ${className || ''}`}>
        {instagramUrl && (
          <button
            className="social-links-row__button"
            onClick={() => handleClick('instagram', instagramUrl)}
            aria-label="Instagram"
          >
            <InstagramIcon size="l" tone="white" />
          </button>
        )}
        {tiktokUrl && (
          <button
            className="social-links-row__button"
            onClick={() => handleClick('tiktok', tiktokUrl)}
            aria-label="TikTok"
          >
            <TikTokIcon size="l" tone="white" />
          </button>
        )}
        {youtubeUrl && (
          <button
            className="social-links-row__button"
            onClick={() => handleClick('youtube', youtubeUrl)}
            aria-label="YouTube"
          >
            <YouTubeIcon size="l" tone="white" />
          </button>
        )}
        {websiteUrl && (
          <button
            className="social-links-row__button"
            onClick={() => handleClick('website', websiteUrl)}
            aria-label="Website"
          >
            <LinkIcon size="l" tone="white" />
          </button>
        )}
      </div>
      <SpecActionSheet
        open={actionSheetOpen}
        onClose={() => {
          setActionSheetOpen(false);
          setSelectedPlatform(null);
        }}
        items={actionItems}
        title={selectedPlatform ? platformLabels[selectedPlatform.platform] : undefined}
      />
    </>
  );
};

export default SocialLinksRow;
